import {
  Alert,
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  PrimaryMultiSelect, PrimarySelect, SubTitle,
} from 'components';
import { IPlugin } from 'pages/dashboard/components/ent-account/components/edit-account/components/plugins/interface';
import { selectPluginData } from 'pages/dashboard/components/ent-account/components/edit-account/selector';
import React from 'react';
import {
  Controller, useFieldArray, useFormContext, useWatch,
} from 'react-hook-form';
import { useSelector } from 'react-redux';
import { IOption } from 'interfaces';
import { ICreateSessionForm } from '../../../../interface';

interface ISessionPluginsProps {
  plugins: IPlugin[];
  context?: string;
}

interface ICombination {
  role: string;
  activities: string[];
}

function getConflictingActivities(
  combinations: ICombination[],
  currentIndex: number,
  currentRole: string,
): Set<string> {
  const conflicts = new Set<string>();
  if (!currentRole) return conflicts;

  combinations.forEach((combo, idx) => {
    if (idx === currentIndex) return;
    if (String(combo.role) !== String(currentRole)) return;
    (combo.activities || []).forEach((act) => conflicts.add(String(act)));
  });

  return conflicts;
}

interface ITappsPluginCombinationsProps {
  pluginKey: string;
}

const TappsPluginCombinations: React.FC<ITappsPluginCombinationsProps> = ({ pluginKey }) => {
  const { control, setError, clearErrors } = useFormContext<ICreateSessionForm>();
  const pluginData = useSelector(selectPluginData);

  const fieldArrayName = `metaData.${pluginKey}.combinations` as any;

  const { fields, append, remove } = useFieldArray({ control, name: fieldArrayName });

  const watchedCombinations: ICombination[] = useWatch({
    control,
    name: fieldArrayName,
    defaultValue: [],
  });

  const roleOptions = React.useMemo((): IOption[] => {
    const data = pluginData?.[pluginKey]?.roles;
    if (Array.isArray(data)) {
      return data.map((item: any) => ({
        id: String(item.id),
        value: String(item.id),
        text: item.name,
      }));
    }
    return [];
  }, [pluginData, pluginKey]);

  const activityOptions = React.useMemo((): IOption[] => {
    const data = pluginData?.[pluginKey]?.activities;
    if (Array.isArray(data)) {
      return data.map((item: any) => ({
        id: String(item.id),
        value: String(item.id),
        text: item.name,
      }));
    }
    return [];
  }, [pluginData, pluginKey]);

  const handleAdd = () => {
    append({ role: '', activities: [] } as ICombination);
  };

  // Compute per-combination conflict sets from watched values
  const conflictSets = React.useMemo(
    () => (watchedCombinations || []).map((_, idx) => {
      const currentRole = String(watchedCombinations[idx]?.role ?? '');
      return getConflictingActivities(watchedCombinations, idx, currentRole);
    }),
    [watchedCombinations],
  );

  // Determine if there are any active conflicts (same role + overlapping activity)
  const hasConflicts = React.useMemo(
    () => conflictSets.some((set) => set.size > 0
      && watchedCombinations.some((combo, idx) => {
        if (conflictSets[idx].size === 0) return false;
        return (combo.activities || []).some((act) => conflictSets[idx].has(String(act)));
      })),
    [conflictSets, watchedCombinations],
  );

  // Sync conflict state into RHF so handleSubmit is blocked while conflicts exist
  React.useEffect(() => {
    const errorField = `metaData.${pluginKey}.combinations` as any;
    if (hasConflicts) {
      setError(errorField, {
        type: 'manual',
        message: 'The same activity cannot be assigned to the same role in multiple combinations.',
      });
    } else {
      clearErrors(errorField);
    }
  }, [hasConflicts, pluginKey, setError, clearErrors]);

  return (
    <div>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Grid item>
          <Typography variant="body1">Role &amp; Activity Combinations</Typography>
        </Grid>
        <Grid item>
          <IconButton color="primary" onClick={handleAdd} size="small" title="Add combination">
            <AddCircleOutlineIcon />
            <Typography variant="body2" sx={{ ml: 0.5 }}>Add</Typography>
          </IconButton>
        </Grid>
      </Grid>

      {hasConflicts && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The same activity cannot be assigned to the same role in multiple combinations.
          Please remove the conflicting activities before saving.
        </Alert>
      )}

      {fields.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, fontStyle: 'italic' }}
        >
          No combinations added. Click &quot;Add&quot; to create a role and activity combination.
        </Typography>
      )}

      {fields.map((field, index) => {
        const currentRole = String(watchedCombinations[index]?.role ?? '');
        const conflicting = conflictSets[index] ?? new Set<string>();

        // Currently selected activities for this combination that are also in conflict
        const currentActivities = watchedCombinations[index]?.activities || [];
        const activeConflicts = currentActivities.filter(
          (act) => conflicting.has(String(act)),
        );

        // Build options — disable those already used by another combo for same role
        const optionsWithDisabled = activityOptions.map((opt) => ({
          ...opt,
          disabled: conflicting.has(String(opt.value)),
        }));

        return (
          <Box
            key={field.id}
            sx={{
              border: activeConflicts.length > 0 ? '1px solid #d32f2f' : '1px solid #E5E5E5',
              borderRadius: '5px',
              p: 2,
              mb: 2,
              background: '#FAFAFA',
            }}
          >
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  Combination #
                  {index + 1}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => remove(index)}
                  title="Remove combination"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            <div className="form-item">
              <Controller
                control={control}
                name={`${fieldArrayName}.${index}.role` as any}
                render={({ field: f }) => (
                  <PrimarySelect
                    {...f}
                    label="Role"
                    placeholder="Select a role"
                    options={roleOptions}
                    value={f.value ?? ''}
                    onChange={(e: any) => f.onChange(e.target.value)}
                  />
                )}
              />
            </div>

            <div className="form-item">
              <Controller
                control={control}
                name={`${fieldArrayName}.${index}.activities` as any}
                render={({ field: f }) => (
                  <>
                    <PrimaryMultiSelect
                      options={optionsWithDisabled}
                      value={Array.isArray(f.value) ? f.value : []}
                      onChange={(e: any) => f.onChange(e.target.value)}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value: any) => {
                            const isConflict = conflicting.has(String(value));
                            return (
                              <Chip
                                key={value}
                                label={
                                  activityOptions.find((opt) => opt.value === value)?.text
                                  || value
                                }
                                color={isConflict ? 'error' : 'primary'}
                              />
                            );
                          })}
                        </Box>
                      )}
                    />
                    {activeConflicts.length > 0 && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {activeConflicts.length === 1
                          ? '1 activity is already used for this role in another combination.'
                          : `${activeConflicts.length} activities are already used for this role in another combination.`}
                      </Typography>
                    )}
                  </>
                )}
              />
            </div>
          </Box>
        );
      })}
    </div>
  );
};

const SessionPlugins: React.FC<ISessionPluginsProps> = ({ plugins, context }) => (
  <div className="form-item">
    <SubTitle>Plugins</SubTitle>

    {plugins.map((plugin: IPlugin) => {
      const visibleConfig = plugin.pluginConfig?.filter(
        (cfg) => !cfg.contexts || !context || cfg.contexts.includes(context),
      );
      if (!visibleConfig || visibleConfig.length === 0) return null;
      return (
        <div key={plugin.id}>
          <Typography sx={{ mb: 1 }}>{plugin.name}</Typography>
          {visibleConfig.map((configItem) => {
            if (configItem.type === 'combinations') {
              return (
                <TappsPluginCombinations
                  key={configItem.key}
                  pluginKey={plugin.key}
                />
              );
            }
            return null;
          })}
        </div>
      );
    })}
  </div>
);

SessionPlugins.defaultProps = {
  context: undefined,
};

export default SessionPlugins;
